'use client';

import Link from 'next/link';
import { Sparkles, CheckCircle2, ArrowRight, CalendarDays, ShieldCheck, QrCode, BarChart3 } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Reveal, RevealItem } from '@/components/ui/ScrollReveal';

const featureCards = [
  {
    icon: QrCode,
    title: 'Fast check-in',
    description: 'QR-based entry that keeps queues short and the attendee flow smooth.',
  },
  {
    icon: CalendarDays,
    title: 'Simple publishing',
    description: 'Create and publish event pages with a clean workflow for every team.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure operations',
    description: 'Built to support registration, payments, and access control safely.',
  },
  {
    icon: BarChart3,
    title: 'Useful analytics',
    description: 'Track registrations, revenue, and engagement without the noise.',
  },
];

const highlights = [
  'QR ticketing and secure check-ins',
  'Flexible forms and registration logic',
  'Live dashboards and analytics',
  'Payment processing and settlements',
];

export default function AboutPage() {
  return (
    <MarketingLayout>
      <section className="relative bg-landing-bg px-4 pb-8 pt-2 sm:px-6 lg:px-8">

        <div className="relative mx-auto max-w-6xl space-y-6 md:space-y-8">
          <Reveal className="elegant-box-hover overflow-hidden rounded-[1.6rem] border border-nature-border bg-bg p-6 shadow-[0_24px_70px_rgba(45,53,54,0.06)] md:rounded-[2rem] md:p-12">
            <div className="flex flex-wrap items-center gap-3">
              <p className="inline-flex items-center gap-2 rounded-full border border-nature-border bg-landing-bg px-4 py-1 text-xs font-semibold tracking-[0.18em] text-landing-muted uppercase">
                <Sparkles className="h-3.5 w-3.5" />
                About Us
              </p>
              <p className="rounded-full border border-nature-border bg-landing-bg px-4 py-1 text-xs font-medium text-landing-muted">
                Built for organizers, attendees, and teams
              </p>
            </div>
            <h1 className="mt-5 max-w-4xl text-3xl font-bold leading-tight text-landing-text sm:text-4xl md:text-6xl">
              About EnthiosHub
            </h1>
            <p className="mobile-readable-copy mt-4 max-w-4xl text-[15px] leading-relaxed text-landing-muted md:text-lg">
              A calm, practical event platform for creating registrations, managing attendees, and running events without unnecessary friction.
            </p>
          </Reveal>

          <div className="grid gap-5 md:gap-6 lg:grid-cols-[1.2fr,0.8fr]">
            <Reveal className="elegant-box-hover rounded-[1.4rem] border border-nature-border bg-bg p-5 shadow-[0_20px_60px_rgba(45,53,54,0.06)] md:rounded-[1.75rem] md:p-10">
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-semibold tracking-[0.18em] text-landing-muted uppercase">Our Mission</p>
                  <h2 className="mt-3 text-2xl font-semibold leading-tight text-landing-text sm:text-3xl md:text-4xl">
                    Make event management accessible, enjoyable, and scalable.
                  </h2>
                  <div className="mt-4 space-y-4 text-base leading-relaxed text-landing-muted md:text-lg">
                    <p>
                      EnthiosHub is designed to help organizers launch better events with less effort and more confidence.
                    </p>
                    <p>
                      From event setup to attendee communication and on-site check-in, the platform keeps the workflow clean and dependable.
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-[0.18em] text-landing-muted uppercase">What We Offer</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 md:gap-4">
                    {featureCards.map((item) => {
                      const Icon = item.icon;
                      return (
                        <RevealItem
                          key={item.title}
                          className="elegant-box-hover rounded-[1.1rem] border border-nature-border bg-landing-bg p-3.5 shadow-[0_10px_30px_rgba(45,53,54,0.04)] md:rounded-[1.25rem] md:p-4"
                        >
                          <div className="flex items-start gap-3">
                            <div className="rounded-xl border border-nature-border bg-bg p-2 text-landing-muted">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-landing-text">{item.title}</h3>
                              <p className="mt-1 text-sm leading-relaxed text-landing-muted">{item.description}</p>
                            </div>
                          </div>
                        </RevealItem>
                      );
                    })}
                  </div>
                </div>

                <RevealItem className="elegant-box-hover rounded-[1.25rem] border border-nature-border bg-landing-bg p-5 text-landing-muted shadow-[0_10px_30px_rgba(45,53,54,0.04)]">
                  <p className="text-sm leading-relaxed md:text-base">
                    Whether you&apos;re hosting a conference, workshop, or community meetup, EnthiosHub gives you a softer, simpler way to run it.
                  </p>
                </RevealItem>
              </div>
            </Reveal>

            <Reveal className="elegant-box-hover space-y-5 rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/8 via-white to-emerald-50/70 p-6 shadow-[0_16px_36px_rgba(45,53,54,0.08)] md:sticky md:top-24 md:self-start h-fit">
              <div>
                <p className="text-xs font-semibold tracking-[0.18em] text-landing-muted uppercase">Built For</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {['Organizers', 'Attendees', 'Teams', 'Communities'].map((item) => (
                    <span key={item} className="rounded-full border border-nature-border/70 bg-bg px-3 py-1 text-sm text-landing-muted shadow-[0_6px_16px_rgba(45,53,54,0.05)]">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold tracking-[0.18em] text-landing-muted uppercase">Platform Highlights</p>
                <ul className="mt-4 space-y-3 text-sm text-landing-muted">
                  {highlights.map((item) => (
                    <li key={item} className="flex items-start gap-3 rounded-2xl border border-nature-border/70 bg-bg px-3 py-3 shadow-[0_8px_18px_rgba(45,53,54,0.05)]">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-landing-muted" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-nature-border/70 bg-bg p-4">
                <p className="text-xs font-semibold tracking-[0.18em] text-landing-muted uppercase">Work With Us</p>
                <p className="mt-3 text-sm leading-relaxed text-landing-muted">
                  Planning an event season or scaling your conference operations?
                </p>
                <Link
                  href="/contact"
                  className="company-cta-btn mobile-tap-btn company-cta-gap inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-text-inverse transition hover:bg-primary-500"
                >
                  Contact Our Team
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal className="elegant-box-hover rounded-[1.6rem] border border-nature-border bg-bg px-5 py-8 shadow-[0_20px_60px_rgba(45,53,54,0.06)] md:rounded-[2rem] md:px-10 md:py-10">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {featureCards.map((item) => {
                const Icon = item.icon;
                return (
                  <RevealItem key={item.title} className="elegant-box-hover rounded-[1.25rem] border border-nature-border bg-landing-bg p-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-bg p-2 text-landing-muted">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-landing-text">{item.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-landing-muted">{item.description}</p>
                      </div>
                    </div>
                  </RevealItem>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
}
